'use client';
import React, { useState } from 'react';
import { Wind, Camera, CheckCircle2, Clock, AlertTriangle, Play, RefreshCw, Battery, Zap, Navigation, Radio,  } from 'lucide-react';

interface DroneUnit {
  id: string;
  name: string;
  status: 'idle' | 'active' | 'charging' | 'maintenance';
  battery: number;
  signal: number;
  county: string;
  lastMission: string;
  flightHours: number;
  imagesCaptures: number;
  model: string;
}

interface DroneMission {
  id: string;
  droneId: string;
  county: string;
  area: string;
  status: 'queued' | 'in-flight' | 'processing' | 'completed' | 'failed';
  coverage: number;
  parcelsDetected: number;
  unregisteredFound: number;
  startTime: string;
  duration: string;
  imageCount: number;
  priority: 'high' | 'medium' | 'low';
}

const DRONES: DroneUnit[] = [
  { id: 'DRN-001', name: 'Eagle-1', status: 'active', battery: 78, signal: 92, county: 'Montserrado', lastMission: '2026-07-06', flightHours: 142, imagesCaptures: 18420, model: 'DJI Matrice 350 RTK' },
  { id: 'DRN-002', name: 'Falcon-2', status: 'idle', battery: 100, signal: 88, county: 'Margibi', lastMission: '2026-07-05', flightHours: 98, imagesCaptures: 12300, model: 'DJI Phantom 4 RTK' },
  { id: 'DRN-003', name: 'Hawk-3', status: 'charging', battery: 34, signal: 0, county: 'Grand Bassa', lastMission: '2026-07-06', flightHours: 211, imagesCaptures: 27800, model: 'DJI Matrice 300 RTK' },
  { id: 'DRN-004', name: 'Kite-4', status: 'maintenance', battery: 0, signal: 0, county: 'Nimba', lastMission: '2026-06-28', flightHours: 320, imagesCaptures: 41200, model: 'Autel EVO II Pro' },
];

const MISSIONS: DroneMission[] = [
  { id: 'MSN-2026-0041', droneId: 'DRN-001', county: 'Montserrado', area: 'Congo Town District', status: 'in-flight', coverage: 67, parcelsDetected: 312, unregisteredFound: 48, startTime: '14:22', duration: '1h 18m', imageCount: 2140, priority: 'high' },
  { id: 'MSN-2026-0040', droneId: 'DRN-002', county: 'Margibi', area: 'Kakata Urban Zone', status: 'processing', coverage: 100, parcelsDetected: 198, unregisteredFound: 22, startTime: '11:05', duration: '2h 10m', imageCount: 3280, priority: 'high' },
  { id: 'MSN-2026-0039', droneId: 'DRN-003', county: 'Grand Bassa', area: 'Buchanan Port Area', status: 'completed', coverage: 100, parcelsDetected: 445, unregisteredFound: 71, startTime: '08:30', duration: '3h 02m', imageCount: 4820, priority: 'medium' },
  { id: 'MSN-2026-0038', droneId: 'DRN-001', county: 'Montserrado', area: 'Paynesville Corridor', status: 'completed', coverage: 100, parcelsDetected: 621, unregisteredFound: 94, startTime: '2026-07-05', duration: '4h 15m', imageCount: 6100, priority: 'high' },
  { id: 'MSN-2026-0037', droneId: 'DRN-004', county: 'Nimba', area: 'Ganta Commercial Zone', status: 'failed', coverage: 42, parcelsDetected: 88, unregisteredFound: 11, startTime: '2026-06-28', duration: '1h 02m', imageCount: 940, priority: 'medium' },
  { id: 'MSN-2026-0042', droneId: 'DRN-002', county: 'Bong', area: 'Gbarnga Central', status: 'queued', coverage: 0, parcelsDetected: 0, unregisteredFound: 0, startTime: 'Scheduled 16:00', duration: '—', imageCount: 0, priority: 'low' },
];

const droneStatusConfig = {
  active: { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.15)', dot: true },
  idle: { label: 'Idle', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', dot: false },
  charging: { label: 'Charging', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)', dot: false },
  maintenance: { label: 'Maintenance', color: '#f87171', bg: 'rgba(248,113,113,0.15)', dot: false },
};

const missionStatusConfig = {
  'in-flight': { label: 'In Flight', color: '#34d399', bg: 'rgba(52,211,153,0.15)', icon: <Navigation size={11} /> },
  processing: { label: 'Processing', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)', icon: <RefreshCw size={11} /> },
  completed: { label: 'Completed', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: <CheckCircle2 size={11} /> },
  queued: { label: 'Queued', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)', icon: <Clock size={11} /> },
  failed: { label: 'Failed', color: '#f87171', bg: 'rgba(248,113,113,0.15)', icon: <AlertTriangle size={11} /> },
};

const priorityColors = { high: '#f87171', medium: '#fbbf24', low: '#94a3b8' };

export default function DroneIntegrationPanel() {
  const [activeTab, setActiveTab] = useState<'fleet' | 'missions' | 'analytics'>('fleet');
  const [scanning, setScanning] = useState(false);

  const handleDispatch = () => {
    setScanning(true);
    setTimeout(() => setScanning(false), 3000);
  };

  const activeMissions = MISSIONS.filter(m => m.status === 'in-flight' || m.status === 'processing').length;
  const totalUnregistered = MISSIONS.filter(m => m.status === 'completed').reduce((s, m) => s + m.unregisteredFound, 0);
  const totalImages = MISSIONS.reduce((s, m) => s + m.imageCount, 0);
  const activeDrones = DRONES.filter(d => d.status === 'active').length;

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)' }}
          >
            <Wind size={17} style={{ color: '#fff' }} />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>
              Drone Aerial Mapping Integration
            </h2>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              Drone-assisted property discovery · Aerial survey missions · Unregistered parcel detection
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeMissions > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
              {activeMissions} Mission{activeMissions > 1 ? 's' : ''} Live
            </div>
          )}
          <button
            onClick={handleDispatch}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:opacity-85 disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)', color: '#fff' }}
          >
            {scanning ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
            {scanning ? 'Dispatching…' : 'Dispatch Drone'}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Active Drones', value: `${activeDrones} / ${DRONES.length}`, color: '#34d399', icon: <Wind size={14} /> },
          { label: 'Live Missions', value: activeMissions.toString(), color: '#60a5fa', icon: <Navigation size={14} /> },
          { label: 'Unregistered Found', value: totalUnregistered.toString(), color: '#f87171', icon: <AlertTriangle size={14} /> },
          { label: 'Images Captured', value: totalImages.toLocaleString(), color: '#fbbf24', icon: <Camera size={14} /> },
        ].map(s => (
          <div
            key={s.label}
            className="rounded-lg px-3 py-2.5 flex items-center gap-3"
            style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: s.color + '20', color: s.color }}>
              {s.icon}
            </div>
            <div>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
              <p className="text-base font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg mb-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        {(['fleet', 'missions', 'analytics'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="flex-1 py-1.5 rounded-md text-xs font-semibold capitalize transition-all"
            style={{
              backgroundColor: activeTab === tab ? 'var(--dark-elevated)' : 'transparent',
              color: activeTab === tab ? '#38bdf8' : 'var(--dark-muted)',
              border: activeTab === tab ? '1px solid var(--dark-border)' : '1px solid transparent',
            }}
          >
            {tab === 'fleet' ? 'Fleet Status' : tab === 'missions' ? 'Mission Log' : 'Coverage Analytics'}
          </button>
        ))}
      </div>

      {/* Fleet Status */}
      {activeTab === 'fleet' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {DRONES.map(drone => {
            const sc = droneStatusConfig[drone.status];
            return (
              <div
                key={drone.id}
                className="rounded-lg p-4"
                style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(56,189,248,0.15)', color: '#38bdf8' }}>
                      <Wind size={15} />
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{drone.name}</p>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{drone.id} · {drone.model}</p>
                    </div>
                  </div>
                  <span
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: sc.bg, color: sc.color }}
                  >
                    {sc.dot && <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ backgroundColor: sc.color }} />}
                    {sc.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs flex items-center gap-1" style={{ color: 'var(--dark-muted)' }}>
                        <Battery size={10} /> Battery
                      </span>
                      <span className="text-xs font-mono-data" style={{ color: drone.battery > 50 ? '#34d399' : drone.battery > 20 ? '#fbbf24' : '#f87171' }}>
                        {drone.battery}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-border)' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${drone.battery}%`,
                          backgroundColor: drone.battery > 50 ? '#34d399' : drone.battery > 20 ? '#fbbf24' : '#f87171',
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs flex items-center gap-1" style={{ color: 'var(--dark-muted)' }}>
                        <Radio size={10} /> Signal
                      </span>
                      <span className="text-xs font-mono-data" style={{ color: drone.signal > 70 ? '#34d399' : drone.signal > 30 ? '#fbbf24' : '#94a3b8' }}>
                        {drone.signal > 0 ? `${drone.signal}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-border)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${drone.signal}%`, backgroundColor: drone.signal > 70 ? '#34d399' : '#fbbf24' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'County', value: drone.county.split(' ')[0] },
                    { label: 'Flight Hrs', value: `${drone.flightHours}h` },
                    { label: 'Images', value: drone.imagesCaptures.toLocaleString() },
                  ].map(s => (
                    <div key={s.label} className="rounded px-2 py-1.5" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
                      <p className="text-xs font-semibold font-mono-data" style={{ color: 'var(--dark-text)' }}>{s.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mission Log */}
      {activeTab === 'missions' && (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: 'var(--dark-surface)' }}>
                {['Mission ID', 'County / Area', 'Drone', 'Status', 'Coverage', 'Unregistered', 'Images', 'Duration'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MISSIONS.map((m, i) => {
                const sc = missionStatusConfig[m.status];
                const drone = DRONES.find(d => d.id === m.droneId);
                return (
                  <tr
                    key={m.id}
                    style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', borderTop: '1px solid var(--dark-border)' }}
                  >
                    <td className="px-3 py-2.5 font-mono-data font-medium" style={{ color: '#38bdf8' }}>{m.id}</td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium" style={{ color: 'var(--dark-text)' }}>{m.county}</p>
                      <p style={{ color: 'var(--dark-muted)' }}>{m.area}</p>
                    </td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: 'var(--dark-muted)' }}>{drone?.name ?? m.droneId}</td>
                    <td className="px-3 py-2.5">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold w-fit"
                        style={{ backgroundColor: sc.bg, color: sc.color }}>
                        {sc.icon}{sc.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-border)' }}>
                          <div className="h-full rounded-full" style={{ width: `${m.coverage}%`, backgroundColor: m.coverage === 100 ? '#34d399' : '#38bdf8' }} />
                        </div>
                        <span className="font-mono-data" style={{ color: 'var(--dark-text)' }}>{m.coverage}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-mono-data font-semibold" style={{ color: m.unregisteredFound > 0 ? '#f87171' : 'var(--dark-muted)' }}>
                      {m.unregisteredFound > 0 ? `+${m.unregisteredFound}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: 'var(--dark-muted)' }}>{m.imageCount > 0 ? m.imageCount.toLocaleString() : '—'}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: 'var(--dark-muted)' }}>{m.duration}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Analytics */}
      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Coverage by county */}
          <div className="lg:col-span-2 rounded-lg p-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>COUNTY COVERAGE PROGRESS</p>
            <div className="space-y-2.5">
              {[
                { county: 'Montserrado', coverage: 82, missions: 14, unregistered: 142 },
                { county: 'Margibi', coverage: 71, missions: 8, unregistered: 22 },
                { county: 'Grand Bassa', coverage: 65, missions: 6, unregistered: 71 },
                { county: 'Nimba', coverage: 38, missions: 4, unregistered: 11 },
                { county: 'Bong', coverage: 12, missions: 1, unregistered: 0 },
                { county: 'Lofa', coverage: 0, missions: 0, unregistered: 0 },
              ].map(row => (
                <div key={row.county}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{row.county}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{row.missions} missions</span>
                      {row.unregistered > 0 && (
                        <span className="text-xs font-semibold" style={{ color: '#f87171' }}>+{row.unregistered} unregistered</span>
                      )}
                      <span className="text-xs font-mono-data font-semibold" style={{ color: row.coverage > 60 ? '#34d399' : row.coverage > 20 ? '#fbbf24' : '#94a3b8' }}>
                        {row.coverage}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-border)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${row.coverage}%`,
                        background: row.coverage > 60
                          ? 'linear-gradient(90deg, #0ea5e9, #34d399)'
                          : row.coverage > 20
                          ? 'linear-gradient(90deg, #0ea5e9, #fbbf24)'
                          : '#94a3b8',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick stats */}
          <div className="space-y-3">
            {[
              { label: 'Total Flight Hours', value: `${DRONES.reduce((s, d) => s + d.flightHours, 0)}h`, color: '#38bdf8', icon: <Wind size={16} /> },
              { label: 'Total Images', value: totalImages.toLocaleString(), color: '#fbbf24', icon: <Camera size={16} /> },
              { label: 'Unregistered Found', value: totalUnregistered.toString(), color: '#f87171', icon: <AlertTriangle size={16} /> },
              { label: 'Est. Tax Recovery', value: `$${(totalUnregistered * 280).toLocaleString()}`, color: '#34d399', icon: <Zap size={16} /> },
            ].map(s => (
              <div
                key={s.label}
                className="rounded-lg px-4 py-3 flex items-center gap-3"
                style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: s.color + '20', color: s.color }}>
                  {s.icon}
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
                  <p className="text-lg font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
                </div>
              </div>
            ))}

            <div
              className="rounded-lg px-4 py-3"
              style={{ background: 'linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(56,189,248,0.08) 100%)', border: '1px solid rgba(56,189,248,0.3)' }}
            >
              <p className="text-xs font-semibold mb-1" style={{ color: '#38bdf8' }}>Next Scheduled Mission</p>
              <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>Gbarnga Central, Bong</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Today 16:00 · DRN-002 Falcon-2</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
